import {kFormatter} from './descriptions'

const sortOptionsLabels: Record<string, string> = {
  'sort:created-desc': 'Newest',
  'sort:created-asc': 'Oldest',
  'sort:comments-desc': 'Most commented',
  'sort:updated-desc': 'Recently updated',
  'sort:reactions-+1-desc': 'Thumbs up',
  'sort:reactions--1-desc': 'Thumbs down',
  'sort:reactions-rocket-desc': 'Rocket',
  'sort:reactions-tada-desc': 'Hooray',
  'sort:reactions-eyes-desc': 'Eyes',
  'sort:reactions-heart-desc': 'Heart',
}

export const LABELS = {
  numberOfResults: (numResults: number) => `${kFormatter(numResults)} ${numResults === 1 ? 'result' : 'results'}`,
  documentTitleForView: (viewName?: string) => (viewName ? `${viewName} · Issues` : `Issues`),
  alpha: 'Alpha',
  beta: 'Beta',
  preview: 'Preview',
  allViews: 'All views',
  defaultViews: 'Default views',
  loadingListingResults: '.. results',
  loadingQueryResults: '.. loading results',
  issueEditingSearchInputAriaLabel: 'Query',
  issueSearchInputAriaLabel: 'Search Issues',
  sectionSearchInputAriaLabel: 'Search PRs',
  issueSearchInputPlaceholder: 'Search Issues',
  sectionSearchInputPlaceholder: 'Search Issues and PRs',
  viewSidebarPaneAriaLabel: 'Navigation sidebar',
  viewsSidebarTriggerAriaLabel: 'Open views',
  issueListPaneAriaLabel: 'Issue list',
  issueCreatePaneTitle: 'Create new issue',
  loadingTooltip: 'Loading...',
  appHeader: 'Issues',
  savedViewsTitlePlaceholder: 'Add a title for your saved view',
  searchResults: 'Search results',
  selectedSubsetOfNumber: (selectedCount: number, totalCount: number) => `${selectedCount} of ${totalCount} selected`,
  singularUnits: (nodeTypes: Set<string | undefined>): string | undefined => {
    if (nodeTypes.has('Issue') && nodeTypes.has('PullRequest')) return 'issue or pull request'
    if (nodeTypes.has('Issue')) return 'issue'
    if (nodeTypes.has('PullRequest')) return 'pull request'
  },
  pluralUnits: (nodeTypes: Set<string | undefined>): string | undefined => {
    if (nodeTypes.has('Issue') && nodeTypes.has('PullRequest')) return 'issues and pull requests'
    if (nodeTypes.has('Issue')) return 'issues'
    if (nodeTypes.has('PullRequest')) return 'pull requests'
  },
  updatingIssues: 'Updating issues',
  viewsTitle: 'Views',
  views: {
    actionsLabel: 'Actions',
    backToViews: 'Back to views',
    color: 'Color',
    copyNamePrefix: (name: string) => `[COPY] ${name}`,
    createDescription: 'Anyone with repository access can see this view.',
    createError: 'Could not create a new view',
    createLink: 'Create view',
    createdBy: (login: string) => `Created by ${login}`,
    defaultName: 'Untitled view',
    delete: 'Delete',
    deleteConfirmationButton: 'Delete',
    deleteContent: (viewName: string) => `Are you sure you want to delete view ${viewName}?`,
    deleteError: 'Could not delete view',
    deleteSuccess: (viewName: string) => `View ${viewName} deleted`,
    deleteSuccessLastView: (viewName: string) => `View ${viewName} deleted. No saved views remaining.`,
    deleteTitle: 'Delete view',
    displayErrorDescription: 'Something went wrong displaying this view. Try reloading the page.',
    displayErrorTitle: 'Couldn’t display this view',
    duplicate: 'Duplicate',
    duplicateBlankNameError: 'Name cannot be blank',
    duplicateConfirmationButton: 'Duplicate view',
    duplicateContent: (viewName: string) => `Create a copy of “${viewName}” as a new saved view.`,
    duplicateError: 'Could not duplicate view',
    duplicateNameLabel: 'New view name',
    duplicateTitle: 'Duplicate view',
    edit: 'Edit view',
    editButtonAriaLabel: 'Saved views menu',
    editViewAriaLabel: (shortcutName: string) => `Edit ${shortcutName}`,
    editViewDescriptionAriaLabel: (shortcutName: string) => `Edit ${shortcutName} description`,
    editViewTitleAriaLabel: (shortcutName: string) => `Edit ${shortcutName} title`,
    emptyDescription: 'Save a search as a view. Views are visible to everyone with repository access.',
    emptyHeading: 'No views yet',
    filterQueryLabel: 'Query',
    filterQueryPlaceholder: 'Search issues',
    icon: 'Icon',
    iconAndColorAnchorAriaLabel: 'Change icon and color',
    indexDisplayErrorDescription: 'We couldn’t display your views. Try reloading the page.',
    indexErrorTitle: 'Something went wrong',
    indexLoadErrorDescription: 'We couldn’t load your views. Please try again.',
    itemCount: (count: number) => `${count} ${count === 1 ? 'item' : 'items'}`,
    lastUpdated: 'Last updated',
    loadErrorDescription: 'Something went wrong loading this view. Please try again.',
    loadErrorTitle: 'Couldn’t load this view',
    loadingSavedViews: 'Loading saved views',
    loadingSelectedTeams: 'Loading selected teams',
    manageTeamsDescription: 'Choose teams that show up in your sidebar.',
    maxViewsDescription: 'Please remove an existing view before creating a new one.',
    maxViewsReached: 'You have reached the maximum number of saved views',
    maxViewsTooltip: 'Maximum number of views reached',
    noTeamsSelectedDescription: 'Choose teams that show up in your sidebar.',
    noTeamsSelectedHeading: 'No teams selected.',
    notFoundDescription: 'It may have been deleted, or the URL may be incorrect.',
    notFoundTitle: 'View not found',
    pin: 'Pin view',
    pinError: 'Could not pin view',
    pinLimitReached: (max: number) => `Maximum of ${max} pins reached. Unpin an item to pin another.`,
    pinned: 'Pinned',
    pinSuccess: (viewName: string) => `View ${viewName} pinned`,
    unpin: 'Unpin view',
    unpinError: 'Could not unpin view',
    unpinSuccess: (viewName: string) => `View ${viewName} unpinned`,
    retrying: 'Retrying',
    saveChangesToNewView: 'Save changes to new view',
    saveChangesToView: 'Save changes',
    saving: 'Saving…',
    selectedTeams: 'Currently selected',
    sortViewsByAriaLabel: 'Sort views by',
    teams: 'Teams',
    unsavedChangesCancel: 'Keep editing',
    unsavedChangesConfirm: 'Discard',
    unsavedChangesContent: 'Are you sure you want to discard them?',
    unsavedChangesTitle: 'You have unsaved changes',
    updateError: 'Could not update the view',
    viewOptionsAriaLabel: (viewName: string) => `${viewName} options`,
    viewsListTitle: 'Saved views',
  },
  filters: {
    author: 'Author',
    authorLabel: 'Filter by author',
    labels: 'Labels',
    labelsLabel: 'Filter by label',
    projects: 'Projects',
    projectsLabel: 'Filter by project',
    milestones: 'Milestones',
    milestonesLabel: 'Filter by milestone',
    assignees: 'Assignees',
    assigneesLabel: 'Filter by assignees',
    issueType: 'Types',
    issueTypeLabel: 'Filter by issue type',
  },
  sortBy: 'Sort by',
  sortByReactions: 'Sort by reactions',
  mostReactions: 'Most reactions',
  sortingLabels: sortOptionsLabels,
  Newest: 'Newest',
  Oldest: 'Oldest',
  RecentlyUpdated: 'Recently updated',
  Default: 'Default',
  Compact: 'Compact',
  Density: 'Density',
  bulkActions: 'Bulk actions',
  markAs: 'Mark as',
  announcePage: (pageNumber: number, totalPages: number, items: number) =>
    `Page ${pageNumber} of ${totalPages} loaded with ${items} items`,
  sidePanelTooltip: 'View issue',
  viewProfile: (login: string) => `View ${login}'s profile`,
}
